/* eslint-disable max-statements */

import { Button, Chip, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import moment from 'moment';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Customer, EORIGIN_DATE_FORMAT_WITH_TIME, RequestFilters, SortDirection } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { SearchBox, Table, TableActions, TableColumnProps } from '@components';
import { TABLE_ITEMS_PER_PAGE } from '@constants';
import { AdditionalFiscalReferenceColumns, CustomersColumns } from '@enums';
import { ContentLayout } from '@features/layout';
import { useCustomers, useDeleteCustomer } from '@hooks/customer';
import { useSettings } from '@hooks/useSettings';

export const CustomerList = () => {
  const navigate = useNavigate();
  const { data: settings } = useSettings();

  const [showModal, setShowModal] = useState(false);
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASC);
  const [tablePageSize, setTablePageSize] = useState(TABLE_ITEMS_PER_PAGE);
  const [page, setPage] = useState(1);

  const {
    data: { totalCustomers: totalCustomersCount } = {
      list: [],
      totalCustomers: 0,
    },
    isLoading: isCustomersLoading,
  } = useCustomers();

  const deleteCustomerMutation = useDeleteCustomer();

  const getRequestFilters = (): RequestFilters => {
    const filter: RequestFilters = {
      searchQuery: tableSearchQuery,
    };

    if (sortField.length && sortDirection.length) {
      filter.sorting = {
        sortField,
        sortDirection,
      };
    }
    return filter;
  };

  const { data: { list: customers } = {} } = useCustomers({ filters: getRequestFilters(), page, pageSize: tablePageSize });

  const loadCustomers = (page = 1, pageSize: number = tablePageSize, tableSortField = '', tableSortDirection = SortDirection.ASC) => {
    setPage(page);
    setTablePageSize(pageSize);
    setSortField(tableSortField);
    setSortDirection(tableSortDirection);
  };

  const toggleAddNewCustomerModal = () => {
    setShowModal(!showModal);

    navigate('/customers/add');
  };

  const searchCustomers = (searchQuery: string) => {
    setTableSearchQuery(searchQuery);
  };

  const handleCustomerDelete = (row: Customer) => {
    deleteCustomerMutation.mutate({ params: { id: row._id } });
  };

  const goToCustomerView = (id?: string) => {
    if (id) {
      navigate(`/customers/${id}`);
    } else {
      navigate('/customers/add');
    }
  };

  const columns: TableColumnProps<Customer>[] = [
    {
      name: CustomersColumns.NAME,
      selector: 'name',
      cell: (row) => row.name,
      sortable: true,
    },
    ...(settings?.isForwarder
      ? [
          {
            name: CustomersColumns.CUSTOMS_BROKER,
            cell: (row: Customer) =>
              row.externalId && row.virtuals?.brokerName ? (
                <Chip checked={false} readOnly color="gray">
                  {String(row.virtuals.brokerName)}
                </Chip>
              ) : (
                ''
              ),
          },
        ]
      : []),
    {
      name: CustomersColumns.DATE,
      selector: 'updatedAt',
      cell: (row) => moment(row.updatedAt).format(EORIGIN_DATE_FORMAT_WITH_TIME),
      sortable: true,
    },
    {
      name: AdditionalFiscalReferenceColumns.ACTIONS,
      sticky: 'right',
      cell: (row) => (
        <TableActions
          actions={[
            {
              icon: IconFactory.view,
              text: 'View details',
              onClick: () => goToCustomerView(row._id),
            },
            {
              icon: IconFactory.delete,
              text: 'Delete',
              onClick: () => openDelete(row),
            },
          ]}
        />
      ),
    },
  ];

  const handlePagination = (page: number, pageSize: number) => {
    loadCustomers(page, pageSize);
  };

  const handleSorting = (direction: SortDirection, field: string) => {
    loadCustomers(1, tablePageSize, field, direction);
  };

  const openDelete = (row: Customer) => {
    modals.openConfirmModal({
      title: 'Confirm customer delete',
      children: <Text>Are you sure you want to delete the customer? Action can't be undone!</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => handleCustomerDelete(row),
    });
  };

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <ContentLayout.Title>Customers</ContentLayout.Title>
        <ContentLayout.Filters>
          <Button onClick={() => goToCustomerView()} variant="outline" size="lg">
            New customer
          </Button>
          <SearchBox handleSearch={searchCustomers} placeholder="Search by customer name" />
        </ContentLayout.Filters>
      </ContentLayout.Header>

      <ContentLayout.Content>
        <Table<Customer>
          columns={columns}
          dataSource={customers ?? []}
          total={totalCustomersCount}
          onPaginationChange={handlePagination}
          onSortingChange={handleSorting}
          isLoading={isCustomersLoading}
          defaultSorting={sortField}
          defaultSortingDirection={sortDirection}
        />
      </ContentLayout.Content>
    </ContentLayout>
  );
};
